import React, { useContext, useMemo } from 'react';
import appNavItemTypeCheckers from '../../../../types/appNavItemTypeCheckers';
import V3EntityNavItem from './V3EntityNavItem';
import NavItem from '../../NavItem';
import BonusPeriodPickerNavItem, {
  LegacyBonusPeriodPickerNavItem,
} from './BonusPeriodPickerNavItem';
import {
  V3AppsContext,
  V3SelectedAppContext,
} from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import ScorecardsContext from '../../../../contexts/ScorecardsContext';
import ReportsContext from '../../../../contexts/ReportsContext';
import DashboardsContext from '../../../../contexts/DashboardsContext';
import useV3HasAccess from '../../../../hooks/useHasAccess/useV3HasAccess';
import BoardsContext from '../../../../contexts/BoardsContext';
import { useSearchParams } from 'react-router-dom';
import FeatureGate, { FEATURE_GATES } from '../../../../components/FeatureGate';

const PrivateDot = () => (
  <div
    style={{
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'flex-end',
    }}
  >
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'var(--color-badge-warning-border)',
      }}
    />
  </div>
);

const AppNavItem = ({
  item,
  app,
}: {
  item: V3.Navigation.Item;
  app: V3.App;
  isAppAdmin: boolean;
}) => {
  const { boards } = useContext(BoardsContext);
  const { scorecards } = useContext(ScorecardsContext);
  const { allReports } = useContext(ReportsContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { selectedProduct } = useContext(V3SelectedAppContext);
  const { availableProductApps } = useContext(V3AppsContext);
  const [searchParams] = useSearchParams();
  const hasAccess = useV3HasAccess();

  const { isPrivate, isHidden } = useMemo((): {
    isPrivate: boolean;
    isHidden: boolean;
  } => {
    if (appNavItemTypeCheckers.isScorecard(item)) {
      const scorecard = scorecards.find((s) => s.id === item.scorecardId);
      if (!scorecard) {
        return {
          isPrivate: true,
          isHidden: true,
        };
      }
      return {
        isPrivate: !scorecard.isV3Published,
        isHidden: !hasAccess({
          resource: scorecard,
          type: 'scorecard',
          typeId: scorecard.id,
        }),
      };
    } else if (appNavItemTypeCheckers.isReport(item)) {
      const report = allReports.find((report) => report.id === item.reportId);
      if (!report) {
        return {
          isPrivate: true,
          isHidden: true,
        };
      }
      return {
        isPrivate: !report.isV3Published,
        isHidden: !hasAccess({
          resource: report,
          type: 'report',
          typeId: report.id,
        }),
      };
    } else if (appNavItemTypeCheckers.isDashboard(item)) {
      const dashboard = allDashboards.find(
        (dashboard) => dashboard.id === item.dashboardId,
      );
      if (!dashboard) {
        return {
          isPrivate: true,
          isHidden: true,
        };
      }
      return {
        isPrivate: !dashboard.isV3Published,
        isHidden: !hasAccess({
          resource: dashboard,
          type: 'dashboard',
          typeId: dashboard.id,
        }),
      };
    } else if (appNavItemTypeCheckers.isBoard(item)) {
      const board = boards.find((board) => board.id === item.boardId);
      if (!board) {
        return {
          isPrivate: true,
          isHidden: true,
        };
      }
      return {
        isPrivate: !board.isV3Published,
        isHidden: !hasAccess({
          resource: board,
          type: 'board',
          typeId: board.id,
        }),
      };
    }

    return {
      isPrivate: false,
      isHidden: false,
    };
  }, [allDashboards, allReports, boards, hasAccess, item, scorecards]);

  const to = useMemo(() => {
    const baseLink = (() => {
      if (!selectedProduct) {
        return `/workspaces/${app.urlSlug}/${item.key}`;
      }

      const subAppsThisUserHasAccessTo = availableProductApps.filter((app) =>
        selectedProduct.appIds.includes(app.id),
      );

      if (subAppsThisUserHasAccessTo.length <= 1) {
        return `/apps/${selectedProduct.urlSlug}/${item.key}`;
      }

      return `/apps/${selectedProduct.urlSlug}/${app.urlSlug}/${item.key}`;
    })();

    if (searchParams.size === 0) {
      return baseLink;
    }

    return `${baseLink}?${searchParams.toString()}`;
  }, [
    app.urlSlug,
    availableProductApps,
    item.key,
    searchParams,
    selectedProduct,
  ]);

  const renderExtra = useMemo(() => {
    if (isPrivate) {
      return <PrivateDot />;
    }
  }, [isPrivate]);

  if (appNavItemTypeCheckers.isBonusPicker(item)) {
    if (appTypeCheckers.isLegacyBonusApp(app)) {
      return <LegacyBonusPeriodPickerNavItem />;
    }
    if (!appTypeCheckers.isBonusApp(app)) {
      return null;
    }
    return <BonusPeriodPickerNavItem app={app} />;
  }

  if (
    appNavItemTypeCheckers.isEntityApp(item) ||
    appNavItemTypeCheckers.isMappedEntityView(item)
  ) {
    return <V3EntityNavItem item={item} />;
  }

  if (appNavItemTypeCheckers.isDriverSetupAndAppAccess(item)) {
    return (
      <FeatureGate featureName={FEATURE_GATES.DRIVER_SETUP_AND_APP_ACCESS}>
        <NavItem
          testId={`link-${item.label}`}
          key={item.key}
          label={item.label}
          to={to}
          icon={item.icon}
          textColor={isPrivate ? 'var(--color-text-tertiary)' : undefined}
          renderExtra={renderExtra}
        />
      </FeatureGate>
    );
  }

  if (isHidden) {
    return null;
  }

  return (
    <NavItem
      testId={`link-${item.label}`}
      key={item.key}
      label={item.label}
      to={to}
      icon={item.icon}
      textColor={isPrivate ? 'var(--color-text-tertiary)' : undefined}
      renderExtra={renderExtra}
    />
  );
};

export default AppNavItem;
